import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Inbox, ArrowLeft, Phone, MessageCircle, StickyNote, Send,
  Loader2, Filter, CheckCircle2, Sparkles, Circle, Plus, Trash2, Zap,
} from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBar";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

export default function Leads() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialSite = params.get("site") || "";
  const initialFilter = params.get("filter") || "all";

  const [sites, setSites] = useState([]);
  const [leads, setLeads] = useState(null);
  const [siteFilter, setSiteFilter] = useState(initialSite);
  const [filter, setFilter] = useState(initialFilter); // all | unread | read
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplSheet, setTplSheet] = useState(false);
  const detailRef = useRef(null);

  const loadTemplates = useCallback(() => {
    api.get("/reply-templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const [{ data: leadsData }, { data: sitesData }] = await Promise.all([
      api.get("/leads"),
      api.get("/sites"),
    ]);
    setLeads(leadsData.leads);
    setSites(sitesData.sites);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (siteFilter) next.set("site", siteFilter);
    if (filter !== "all") next.set("filter", filter);
    setParams(next, { replace: true });
  }, [siteFilter, filter, setParams]);

  const filtered = useMemo(() => {
    if (!leads) return [];
    return leads.filter((l) => {
      if (siteFilter && l.site_id !== siteFilter) return false;
      if (filter === "unread") return l.status === "new";
      if (filter === "read") return l.status !== "new";
      return true;
    });
  }, [leads, siteFilter, filter]);

  const openLead = async (lead) => {
    setSelected(lead);
    setNote("");
    // Mobile: scroll the detail panel into view so the tap has visible feedback.
    requestAnimationFrame(() => {
      detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    if (lead.status === "new") {
      try {
        const { data } = await api.patch(`/leads/${lead.id}`, { status: "read" });
        setLeads((ls) => ls.map((x) => x.id === lead.id ? data.lead : x));
        setSelected(data.lead);
        window.dispatchEvent(new CustomEvent("wt:leads-refresh"));
      } catch { /* non-blocking */ }
    }
  };

  const saveNote = async () => {
    if (!selected || !note.trim()) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/leads/${selected.id}/notes`, { text: note.trim() });
      setLeads((ls) => ls.map((x) => x.id === selected.id ? data.lead : x));
      setSelected(data.lead);
      setNote("");
      toast.success("Note saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save note");
    } finally { setSavingNote(false); }
  };

  const waHref = (l, txt) => {
    const shop = l.business_name || "our shop";
    const fallback = `Namaste ${l.name}, thank you for your enquiry on ${shop}. How can we help you?`;
    const msg = (txt || fallback)
      .replaceAll("{name}", l.name || "there")
      .replaceAll("{shop}", shop);
    return `https://wa.me/91${l.phone}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="min-h-screen">
      <TopBar active="leads" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white inline-flex items-center gap-1.5 text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-3">
              <Inbox className="w-7 h-7 text-[#C9A24B]" /> Lead Inbox
            </h1>
            <p className="text-zinc-400 text-sm mt-1">Every enquiry from your live website lands here.</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold inline-flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5" /> Filter
          </span>
          <select data-testid="leads-site-filter" value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}
            className="bg-[#161616] border border-[#27272A] text-sm text-zinc-200 rounded-xl px-3 py-2 focus:outline-none focus:border-[#C9A24B]">
            <option value="">All sites</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.business_name}</option>)}
          </select>
          <div className="inline-flex rounded-xl border border-[#27272A] p-1 bg-[#161616]">
            {[["all", "All"], ["unread", "Unread"], ["read", "Read"]].map(([k, l]) => (
              <button key={k} data-testid={`leads-filter-${k}`} onClick={() => setFilter(k)}
                className={`px-3 py-1.5 text-xs rounded-lg font-semibold transition-all ${filter === k ? "bg-[#C9A24B] text-[#0A0A0A]" : "text-zinc-400 hover:text-white"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {leads === null ? (
          <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 text-[#C9A24B] wt-spin" /></div>
        ) : filtered.length === 0 ? (
          <EmptyLeads onCreate={() => navigate("/create")} filter={filter} />
        ) : (
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-5">
            <div data-testid="leads-inbox-list" className="space-y-3">
              {filtered.map((l) => (
                <LeadCard key={l.id} lead={l} active={selected?.id === l.id} onOpen={() => openLead(l)} />
              ))}
            </div>
            <div ref={detailRef} className="lg:sticky lg:top-24 lg:self-start scroll-mt-24">
              {selected ? (
                <LeadDetail lead={selected} note={note} setNote={setNote} savingNote={savingNote}
                  onSave={saveNote} defaultWaHref={waHref(selected)}
                  templates={templates} waFromTemplate={(t) => waHref(selected, t)}
                  onManageTemplates={() => setTplSheet(true)} />
              ) : (
                <div className="rounded-2xl border border-dashed border-[#27272A] p-8 text-center text-zinc-400">
                  <Sparkles className="w-6 h-6 text-[#C9A24B] mx-auto mb-2" />
                  Tap a lead to reply, note or call.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {tplSheet && (
        <TemplatesSheet templates={templates} onClose={() => setTplSheet(false)}
          onAdded={(t) => setTemplates((ts) => [...ts, t])}
          onDeleted={(id) => setTemplates((ts) => ts.filter((x) => x.id !== id))} />
      )}
    </div>
  );
}

function TemplatesSheet({ templates, onClose, onAdded, onDeleted }) {
  const [label, setLabel] = useState("");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!label.trim() || !text.trim()) return toast.error("Label and message are required");
    setSaving(true);
    try {
      const { data } = await api.post("/reply-templates", { label: label.trim(), text: text.trim() });
      onAdded(data.template);
      setLabel(""); setText("");
      toast.success("Template saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/reply-templates/${id}`);
      onDeleted(id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" data-testid="reply-templates-sheet" onClick={onClose}>
      <div className="bg-[#161616] border-l border-[#27272A] w-full max-w-md h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-xl font-bold flex items-center gap-2"><Zap className="w-5 h-5 text-[#C9A24B]" /> Reply Templates</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><Plus className="w-5 h-5 rotate-45" /></button>
        </div>
        <p className="text-sm text-zinc-400 mb-5">Use <code className="text-[#E5C378] text-[11px]">{`{name}`}</code> for the customer and <code className="text-[#E5C378] text-[11px]">{`{shop}`}</code> for your business.</p>

        <div className="space-y-2 mb-6">
          {templates.map((t) => (
            <div key={t.id} className="rounded-xl border border-[#27272A] bg-[#0A0A0A] p-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-sm font-semibold text-[#E5C378]">{t.label}</div>
                {!t.default && (
                  <button data-testid={`template-delete-${t.id}`} onClick={() => del(t.id)}
                    className="text-zinc-500 hover:text-[#EF4444] p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
              <p className="text-xs text-zinc-400 whitespace-pre-wrap">{t.text}</p>
              {t.default && <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-1">Default</div>}
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-[#27272A] bg-[#0A0A0A] p-4">
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-2">Add your own</div>
          <input data-testid="template-new-label" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40}
            placeholder="Label e.g. Delivery Charges"
            className="w-full bg-[#161616] border border-[#27272A] rounded-lg p-2.5 text-sm text-zinc-100 mb-2 focus:outline-none focus:border-[#C9A24B]" />
          <textarea data-testid="template-new-text" value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={500}
            placeholder="Hi {name}, our delivery charges are..."
            className="w-full bg-[#161616] border border-[#27272A] rounded-lg p-2.5 text-sm text-zinc-100 mb-2 focus:outline-none focus:border-[#C9A24B]" />
          <button data-testid="template-add-btn" onClick={add} disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] font-bold py-2.5 rounded-lg disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 wt-spin" /> : <Plus className="w-4 h-4" />} Add Template
          </button>
        </div>
      </div>
    </div>
  );
}

function LeadCard({ lead, active, onOpen }) {
  const unread = lead.status === "new";
  return (
    <button data-testid={`lead-item-card-${lead.id}`} onClick={onOpen}
      className={`w-full text-left rounded-2xl border p-4 transition-all ${active ? "border-[#C9A24B] bg-[#C9A24B]/5"
        : unread ? "border-[#C9A24B]/30 bg-[#161616] hover:border-[#C9A24B]/60"
        : "border-[#27272A] bg-[#161616] hover:border-[#3a3a3d]"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {unread ? (
            <span className="mt-1 w-2 h-2 rounded-full bg-[#C9A24B] shadow-[0_0_10px_#C9A24B] shrink-0" />
          ) : (
            <Circle className="w-2 h-2 mt-1 text-zinc-600 shrink-0" />
          )}
          <div className="min-w-0">
            <div className={`font-semibold truncate ${unread ? "text-white" : "text-zinc-300"}`}>
              {lead.name} <span className="text-zinc-500 font-normal">· +91 {lead.phone}</span>
            </div>
            <div className="text-sm text-zinc-400 truncate mt-0.5">{lead.message || "No message"}</div>
            <div className="text-xs text-zinc-500 mt-1">{lead.business_name} · {timeAgo(lead.created_at)}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {lead.notes?.length ? (
            <span className="text-[10px] font-semibold text-[#E5C378] bg-[#C9A24B]/10 border border-[#C9A24B]/25 px-1.5 py-0.5 rounded">
              {lead.notes.length} note{lead.notes.length > 1 ? "s" : ""}
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function LeadDetail({ lead, note, setNote, savingNote, onSave, defaultWaHref, templates, waFromTemplate, onManageTemplates }) {
  return (
    <div data-testid="lead-detail-view" className="rounded-2xl border border-[#27272A] bg-[#161616] overflow-hidden">
      <div className="p-5 border-b border-[#27272A]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-xl">{lead.name}</h3>
            <p className="text-sm text-zinc-400">+91 {lead.phone}</p>
            <p className="text-xs text-zinc-500 mt-1">{lead.business_name} · {timeAgo(lead.created_at)}</p>
          </div>
          {lead.status !== "new" && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#22C55E] bg-[#22C55E]/10 border border-[#22C55E]/30 px-2 py-1 rounded-full">
              <CheckCircle2 className="w-3 h-3" /> Read
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <a data-testid={`lead-action-call-btn-${lead.id}`} href={`tel:+91${lead.phone}`}
            className="inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#1C1C1E] border border-[#27272A] hover:border-[#C9A24B]/50 text-sm font-semibold transition-all">
            <Phone className="w-4 h-4 text-[#C9A24B]" /> Call
          </a>
          <a data-testid={`lead-action-whatsapp-btn-${lead.id}`} href={defaultWaHref} target="_blank" rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#25D366] hover:bg-[#22bf5b] text-[#04120a] text-sm font-bold transition-all">
            <MessageCircle className="w-4 h-4" /> WhatsApp Reply
          </a>
        </div>
      </div>

      {/* Reply templates — 1-tap sends prefilled WhatsApp */}
      <div className="px-5 pt-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#C9A24B]" />
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Quick reply templates</div>
          </div>
          <button data-testid="lead-manage-templates-btn" onClick={onManageTemplates}
            className="text-[11px] text-[#E5C378] hover:underline">Manage</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {templates.slice(0, 8).map((t) => (
            <a key={t.id} data-testid={`lead-template-chip-${t.id}`} href={waFromTemplate(t.text)}
              target="_blank" rel="noreferrer" title={t.text.replaceAll("{name}", lead.name).replaceAll("{shop}", lead.business_name || "our shop")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-[#C9A24B]/10 border border-[#C9A24B]/30 text-[#E5C378] hover:bg-[#C9A24B]/20 transition-all">
              <MessageCircle className="w-3 h-3" /> {t.label}
            </a>
          ))}
        </div>
      </div>

      {lead.message && (
        <div className="p-5 border-b border-[#27272A]">
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-2">Customer message</div>
          <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{lead.message}</p>
        </div>
      )}

      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <StickyNote className="w-4 h-4 text-[#C9A24B]" />
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Internal notes</div>
        </div>
        <div className="space-y-2 mb-3">
          {(lead.notes || []).length === 0 && (
            <p className="text-sm text-zinc-500">No notes yet. Jot a quick reminder for yourself.</p>
          )}
          {(lead.notes || []).map((n) => (
            <div key={n.id} className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3 text-sm text-zinc-200">
              <p className="whitespace-pre-wrap">{n.text}</p>
              <div className="text-[11px] text-zinc-500 mt-1">{timeAgo(n.created_at)}</div>
            </div>
          ))}
        </div>
        <textarea data-testid="lead-note-textarea" value={note} onChange={(e) => setNote(e.target.value)}
          rows={2} maxLength={500}
          placeholder="e.g. Called customer, appointment fixed for Saturday 11 AM"
          className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3 text-sm text-zinc-100 focus:outline-none focus:border-[#C9A24B] transition-all" />
        <button data-testid="lead-save-note-btn" onClick={onSave} disabled={savingNote || !note.trim()}
          className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] font-bold text-sm disabled:opacity-50 transition-all active:scale-[0.98]">
          {savingNote ? <Loader2 className="w-4 h-4 wt-spin" /> : <Send className="w-4 h-4" />} Save Note
        </button>
      </div>
    </div>
  );
}

function EmptyLeads({ filter, onCreate }) {
  const isUnread = filter === "unread";
  return (
    <div className="rounded-3xl border border-dashed border-[#27272A] py-20 text-center">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-[#C9A24B]/10 flex items-center justify-center mb-5">
        <Inbox className="w-8 h-8 text-[#C9A24B]" />
      </div>
      <h3 className="font-display text-xl font-bold">
        {isUnread ? "No unread leads" : "Your first lead will land here"}
      </h3>
      <p className="text-zinc-400 text-sm mt-2 mb-6 max-w-md mx-auto">
        {isUnread
          ? "Everything is caught up. New enquiries will show up right here."
          : "Share your website link on WhatsApp & Instagram to start getting customer enquiries."}
      </p>
      {!isUnread && (
        <button onClick={onCreate}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A]">
          <Sparkles className="w-4 h-4" /> Create another site
        </button>
      )}
    </div>
  );
}
